import { z } from 'zod';

/**
 * SPEC-008 §5/§6/§7/plan §3 — canonical metric value model. This module is
 * the single source of truth for the metric type system: framework-free,
 * no React/Zustand/D1/network/AI dependency (METRIC-PRINCIPLE-002,
 * METRIC-REQ-012).
 */

// --- Origin / Status -------------------------------------------------------

/** Only `manual`/`calculated` are active in V1; the rest are reserved for
 * forward compatibility (benchmark/AI/import origins land in later SPECs). */
export const METRIC_ORIGINS = ['manual', 'calculated', 'benchmark', 'ai', 'imported'] as const;
export type MetricOrigin = (typeof METRIC_ORIGINS)[number];
export const ACTIVE_METRIC_ORIGINS = ['manual', 'calculated'] as const satisfies readonly MetricOrigin[];

export const METRIC_STATUSES = ['known', 'unknown', 'not_applicable', 'invalid'] as const;
export type MetricStatus = (typeof METRIC_STATUSES)[number];

// --- Warnings ----------------------------------------------------------------

/** SPEC-008 §64 — canonical warning codes. */
export const METRIC_WARNING_CODES = [
  'missing_input',
  'division_by_zero',
  'incomplete_cost_basis',
  'unallocated_flow',
  'overallocated_flow',
  'partial_merge',
  'cycle_requires_manual_anchor',
  'ambiguous_metric_role',
  'unsupported_metric',
] as const;
export type MetricWarningCode = (typeof METRIC_WARNING_CODES)[number];

export interface MetricWarning {
  code: MetricWarningCode;
  message: string;
  /** Node/edge id(s) this warning is about, when applicable. */
  refs?: readonly string[];
}

export function warning(
  code: MetricWarningCode,
  message: string,
  refs?: readonly string[],
): MetricWarning {
  return refs ? { code, message, refs } : { code, message };
}

// --- Explanation ---------------------------------------------------------

/** SPEC-008 §65/§66 — every calculated metric can explain itself. */
export interface MetricExplanation {
  metric: string;
  formula: string;
  inputs: Record<string, number | string | null>;
  result: number | null;
  unit?: string;
  /** Node/edge ids that contributed to an aggregate (plan §22/T069). */
  contributors?: readonly string[];
}

// --- Money / Time / Goal ---------------------------------------------------

/** METRIC-PRINCIPLE-005: money is always integer minor units (e.g. cents). */
export type MoneyMinorUnits = number;

export function isValidMoneyMinorUnits(value: number): boolean {
  return Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

/** METRIC-PRINCIPLE-006: ratios/percentages are always 0..1. */
export type Ratio = number;

export function isValidRatio(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= 1;
}

export function isValidNonNegativeNumber(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

export const TIME_UNITS = ['minutes', 'hours', 'days'] as const;
export type TimeUnit = (typeof TIME_UNITS)[number];

export const TimeValueSchema = z.object({
  value: z.number().finite().nonnegative(),
  unit: z.enum(TIME_UNITS),
});
export type TimeValue = z.infer<typeof TimeValueSchema>;

/** SPEC-008 §26 — a goal always names which metric it targets. */
export const GOAL_TARGET_METRICS = [
  'entries',
  'exits',
  'conversion',
  'cost',
  'revenue',
  'cpl',
  'cac',
  'averageTicket',
] as const;
export type GoalTargetMetric = (typeof GOAL_TARGET_METRICS)[number];

export const GOAL_PERIODS = ['day', 'week', 'month', 'quarter', 'year'] as const;
export type GoalPeriod = (typeof GOAL_PERIODS)[number];

export const GoalValueSchema = z.object({
  metric: z.enum(GOAL_TARGET_METRICS),
  value: z.number().finite(),
  period: z.enum(GOAL_PERIODS).optional(),
});
export type GoalValue = z.infer<typeof GoalValueSchema>;

// --- Metric input (persisted) ----------------------------------------------

/**
 * SPEC-008 §77/plan §4 — the only thing persisted for a metric field.
 * `calculated result lives outside canonical input` (plan §5/§30):
 * `MetricInput` never stores a calculated value, only the manual override
 * (if any) and whether the field is currently in manual or auto mode.
 */
/**
 * SPEC-008 §80/METRIC-REQ — different fields need different valid ranges:
 * money/count fields reject negative manual overrides, ratios stay 0..1.
 * All three share the same runtime shape (`MetricInput`), just different
 * validation on `manualValue`.
 */
export const MoneyMetricInputSchema = z.object({
  mode: z.enum(['manual', 'auto']).default('auto'),
  manualValue: z.number().finite().nonnegative().nullable().optional(),
});
export const CountMetricInputSchema = z.object({
  mode: z.enum(['manual', 'auto']).default('auto'),
  manualValue: z.number().finite().nonnegative().nullable().optional(),
});
export const RatioMetricInputSchema = z.object({
  mode: z.enum(['manual', 'auto']).default('auto'),
  manualValue: z.number().finite().min(0).max(1).nullable().optional(),
});
/** Unconstrained variant, used only where no range restriction applies. */
export const MetricInputSchema = z.object({
  mode: z.enum(['manual', 'auto']).default('auto'),
  manualValue: z.number().finite().nullable().optional(),
});
export type MetricInput = z.infer<typeof MetricInputSchema>;

export const TimeMetricInputSchema = z.object({
  mode: z.enum(['manual', 'auto']).default('auto'),
  manualValue: TimeValueSchema.nullable().optional(),
});
export type TimeMetricInput = z.infer<typeof TimeMetricInputSchema>;

export const GoalMetricInputSchema = z.object({
  mode: z.literal('manual').default('manual'),
  manualValue: GoalValueSchema.nullable().optional(),
});
export type GoalMetricInput = z.infer<typeof GoalMetricInputSchema>;

// --- Metric result (runtime only, never persisted) -------------------------

/**
 * SPEC-008 §9/plan §5 — what the engine hands back to the UI. Never
 * persisted (plan §30/T079): always re-derived from canonical inputs.
 */
export interface MetricResult<T = number> {
  status: MetricStatus;
  /** The value the UI should treat as "the" value: manual when present, else calculated. */
  effectiveValue: T | null;
  /** The engine's own computed value, always available for display even under a manual override. */
  calculatedCandidate: T | null;
  origin: MetricOrigin | null;
  warnings: MetricWarning[];
  explanation?: MetricExplanation;
}

export function unknownResult<T = number>(warnings: MetricWarning[] = []): MetricResult<T> {
  return { status: 'unknown', effectiveValue: null, calculatedCandidate: null, origin: null, warnings };
}

export function notApplicableResult<T = number>(): MetricResult<T> {
  return { status: 'not_applicable', effectiveValue: null, calculatedCandidate: null, origin: null, warnings: [] };
}

export function invalidResult<T = number>(warnings: MetricWarning[]): MetricResult<T> {
  return { status: 'invalid', effectiveValue: null, calculatedCandidate: null, origin: null, warnings };
}

/**
 * METRIC-REQ-001 — resolves manual/calculated precedence for a single
 * numeric field. `calculatedCandidate` is always the engine's own answer
 * (or null), independent of whether manual wins.
 */
export function resolveMetricValue(
  input: MetricInput | undefined,
  calculatedCandidate: number | null,
  candidateWarnings: MetricWarning[] = [],
  explanation?: MetricExplanation,
): MetricResult {
  const hasManual = input?.mode === 'manual' && input.manualValue !== null && input.manualValue !== undefined;
  if (hasManual) {
    return {
      status: 'known',
      effectiveValue: input!.manualValue!,
      calculatedCandidate,
      origin: 'manual',
      warnings: candidateWarnings,
      explanation,
    };
  }
  if (calculatedCandidate === null) {
    return { status: 'unknown', effectiveValue: null, calculatedCandidate: null, origin: null, warnings: candidateWarnings, explanation };
  }
  return {
    status: 'known',
    effectiveValue: calculatedCandidate,
    calculatedCandidate,
    origin: 'calculated',
    warnings: candidateWarnings,
    explanation,
  };
}

// --- Node / edge metric keys -------------------------------------------------

export const NODE_METRIC_KEYS = [
  'cost',
  'entries',
  'exits',
  'conversion',
  'revenue',
  'cpl',
  'cac',
  'averageTicket',
  'averageTime',
  'goal',
] as const;
export type NodeMetricKey = (typeof NODE_METRIC_KEYS)[number];

/** SPEC-008 §58 — roles a node instance can carry for funnel aggregation. */
export const METRIC_ROLES = ['lead', 'new_customer', 'revenue', 'media_cost', 'total_cost'] as const;
export type MetricRole = (typeof METRIC_ROLES)[number];

// --- Persisted node/edge metrics shape --------------------------------------

/** SPEC-008 §76 — `node.data.metrics`. All keys optional; `{}` is valid (T028). */
export const NodeMetricsSchema = z
  .object({
    cost: MoneyMetricInputSchema.optional(),
    entries: CountMetricInputSchema.optional(),
    exits: CountMetricInputSchema.optional(),
    conversion: RatioMetricInputSchema.optional(),
    revenue: MoneyMetricInputSchema.optional(),
    cpl: MoneyMetricInputSchema.optional(),
    cac: MoneyMetricInputSchema.optional(),
    averageTicket: MoneyMetricInputSchema.optional(),
    averageTime: TimeMetricInputSchema.optional(),
    goal: GoalMetricInputSchema.optional(),
    /** T012 — instance-level role override without changing node type. */
    roleOverrides: z.array(z.enum(METRIC_ROLES)).optional(),
    /** §28 — per cost metric; defaults to true where semantically applicable. */
    includeInFunnelInvestment: z.boolean().optional(),
  })
  .partial()
  .default({});
export type NodeMetrics = z.infer<typeof NodeMetricsSchema>;

/** SPEC-008 §17/§76/METRIC-REQ-005 — `edge.data.metrics`, conversion is always 0..1. */
export const EdgeMetricsSchema = z
  .object({
    conversion: RatioMetricInputSchema.optional(),
  })
  .partial()
  .default({});
export type EdgeMetrics = z.infer<typeof EdgeMetricsSchema>;
